import { EquatorialCoord } from "@hscmap/angle";
import { FrameStore } from "@/modules/base";
import { CatalogOverplotModule, IRowRef } from "@/modules/CatalogOverplotModule";
import { CatalogOverplot } from "@/modules/CatalogOverplotModule/CatalogOverplot";
import { MarkerLayer } from "@/modules/CatalogOverplotModule/Marker";
import { MarkerStyle } from "@/modules/CatalogOverplotModule/Marker/marker";
import { Frame, store } from "@/store";
import { Component, Prop, Vue } from "vue-property-decorator";
import { freeze } from '@/utils/freeze';
import Loading from '@/components/Loading.vue'

const idRegexp = /^[a-z_]\w*$/i

@Component({
  components: {
    Plotly: () => ({
      component: import('@/components/Plotly'),
      loading: Loading,
      delay: 0,
    }) as any,
  },
})
export default class extends Vue {
  @Prop({ required: true })
  module!: CatalogOverplotModule

  @Prop({ required: true })
  frameStore!: FrameStore<typeof CatalogOverplotModule>

  @Prop({ required: true })
  co!: CatalogOverplot

  @Prop({ required: true })
  frame!: Frame

  focusOnHover = false

  get tableFilteredIndices() {
    return this.co.tableFilterFunc ?
      this.co.rows.map((row, i) => i).filter((i) => this.co.tableFilterFunc!(i)) :
      this.co.rows.map((row, i) => i)
  }

  get plotlyData(): Array<Partial<Plotly.ScatterData>> {
    const indices = this.tableFilteredIndices
    let x: number[] = []
    let y: number[] = []
    try {
      x = freeze(indices.map((i) => this.xFunc(i)))
      y = freeze(indices.map((i) => this.yFunc(i)))
    } catch (e) {
      alert(`Error: plotly data: ${e}`)
    }
    return [{
      x,
      y,
      // type: 'scattergl',
      type: 'scatter',
      mode: 'markers',
    }]
  }

  get plotlyLayout(): Partial<Plotly.Layout> {
    return {
      plot_bgcolor: 'transparent',
      paper_bgcolor: 'transparent',
      margin: {
        l: 80,
        t: 20,
        b: 20,
        r: 0,
      },
      autosize: true,
      font: {
        color: 'white',
      },
      xaxis: { gridcolor: 'rgba(255, 255, 255, 0.5)' },
      yaxis: { gridcolor: 'rgba(255, 255, 255, 0.5)' },
      hovermode: 'closest',
    }
  }

  get plotlyConfig(): Partial<Plotly.Config> {
    return {
      // scrollZoom: true,
      displayModeBar: true,
    }
  }

  plotlySelected(e: Plotly.PlotSelectionEvent) {
    if (e.points.length === 0) {
      this.plotly().deselect()
      this.co.chartSelectedIndices = null
    } else {
      const ids: number[] = []
      const tfi = this.tableFilteredIndices
      for (const point of e.points) {
        ids.push(tfi[point.pointNumber])
      }
      this.co.chartSelectedIndices = ids
    }
  }

  plotlyDeselect() {
    this.co.chartSelectedIndices = null
  }

  plotlyHover(e: Plotly.PlotMouseEvent) {
    const j = e.points[0].pointNumber
    const i = this.tableFilteredIndices[j]
    this.focus(i)
    this.focusOnHover && this.goto(i)
  }

  plotlyUnhover(e: Plotly.PlotMouseEvent) {
    this.clearFocus()
  }

  plotlyClick(e: Plotly.PlotMouseEvent) {
    const i = this.tableFilteredIndices[e.points[0].pointNumber]
    this.goto(i)
  }

  private goto(i: number) {
    const coord = EquatorialCoord.fromXyz(this.co.coord3[i])
    store.globe(this.frame).viewFactory.jumpTo({}, { coord, duration: 100, keepFovy: true })

  }

  private varDecs() {
    const decs = ['var $ = {}']
    this.co.header.forEach((colname, i) => {
      if (idRegexp.test(colname)) {
        decs.push(`var ${colname} = $row[${i}]`)
      } else {
        decs.push(`$[${JSON.stringify(colname)}] = $row[${i}]`)
      }
    })
    return decs.join(';')
  }

  private rowEvaluator<T>(js: string): (i: number) => T {
    const jsf = `(function($rows){
      return function($i) {
        var $row = $rows[$i];
        ${this.varDecs()};
        return (${js});
      }
    })`
    try {
      // tslint:disable-next-line:no-eval
      return eval(jsf)(this.co.rows)
    } catch (e) {
      console.warn(jsf)
      throw e
    }
  }

  jsFor(colname: string) {
    return idRegexp.test(colname) ? colname : `$[${JSON.stringify(colname)}]`
  }

  private sourceCand(i: number) {
    const merit = [/mag(?!err|sigma)/, /flux(?!err|sigma)/, /err/, /sigma/]
    const score = (h: string) => {
      for (let j = 0; j < merit.length; ++j) {
        if (h.search(merit[j]) >= 0) {
          return j
        }
      }
      return merit.length
    }
    const cands = this.co.header.slice().sort((a, b) => score(a) - score(b))
    return cands[i]
  }

  xFuncJs = this.jsFor(this.sourceCand(0))
  xFuncJsError: null | string = null
  get xFunc() {
    const js = this.xFuncJs
    this.xFuncJsError = null
    if (js.trim().length > 0) {
      try { return this.rowEvaluator<number>(js) } catch (e) { this.xFuncJsError = e }
    }
    return (i: number) => NaN
  }

  yFuncJs = this.jsFor(this.sourceCand(1))
  yFuncJsError: null | string = null
  get yFunc() {
    const js = this.yFuncJs
    this.yFuncJsError = null
    if (js.trim().length > 0) {
      try { return this.rowEvaluator<number>(js) } catch (e) { this.yFuncJsError = e }
    }
    return (i: number) => NaN
  }

  plotly() {
    // import * as Plotly from 'plotly.js-dist';
    return this.$refs.chart as any
  }

  private focusLayer?: MarkerLayer

  private focus(i: number) {
    this.clearFocus()
    const globe = store.globe(this.frame)
    const xyz = this.co.coord3[i]
    this.focusLayer = new MarkerLayer([xyz], [1, 1, 1, 1], MarkerStyle.CROSS3)
    globe.addLayer(this.focusLayer)
    const rowRef: IRowRef = { co: this.co, i }
    this.frameStore.$emit('hover-object', rowRef)
  }

  private clearFocus() {
    const globe = store.globe(this.frame)
    this.focusLayer && globe.removeLayer(this.focusLayer)
    this.focusLayer = undefined
    this.frameStore.$emit('hover-object', null)
  }
}
